import { PurchasableProduct } from '@accedo/one-plugin-sdk';

import { Button } from '#/components/Button/Button';
import { FullPageModal } from '#/components/Modal/FullPageModal';
import { StatusTitle } from '#/components/Status/Status.styled';
import { translatePrice } from '#/plugins/paywall/utils';
import { AppStatus, ButtonVariant } from '#/service/constants';
import { tb } from '#/store/translation/translation.store';

import { StyledSpinner } from '../Splash/Splash.styled';
import { Status } from '../Status/Status';
import { useChangeProductConfirmation } from './ChangeProductConfirmation.hook';
import { ChangeProductSuccess } from './ChangeProductSuccess';
import { ButtonContainer, Message } from './PlanPurchaseFlow.styled';
import { Actions, PurchaseFlowStore } from './PlanPurchaseFlow.types';

export interface ChangeProductConfirmationProps {
  store: PurchaseFlowStore;
  purchasableProducts: PurchasableProduct[];
  actions: Actions;
  onErrorCancel: () => void;
  onChangeCancel: () => void;
}

export const ChangeProductConfirmation = ({
  store,
  purchasableProducts,
  actions: { handleClose },
  onErrorCancel,
  onChangeCancel,
}: ChangeProductConfirmationProps) => {
  const {
    handleChangeProduct,
    isChangeLoading,
    isChangeComplete,
    currentSubscription,
    nextSubscription,
    error,
  } = useChangeProductConfirmation(store, purchasableProducts);

  if (error) {
    return (
      <Status
        applyDefaultGradient={false}
        error={error}
        onDismiss={onErrorCancel}
        status={AppStatus.Error}
      />
    );
  }

  if (!nextSubscription || !currentSubscription) {
    return <StyledSpinner />;
  }

  if (isChangeComplete && nextSubscription) {
    return (
      <ChangeProductSuccess
        nextSubscriptionDetails={nextSubscription}
        onClose={handleClose}
      />
    );
  }

  return (
    <FullPageModal applyDefaultGradient={false}>
      <FullPageModal.CenteredContent>
        <StatusTitle>{tb('iap.confirmation.upgrade.title')}</StatusTitle>
        <Message>
          {tb('iap.confirmation.upgrade.currentPlan', {
            title: currentSubscription?.title,
            price: translatePrice(
              currentSubscription?.price,
              currentSubscription?.currency,
              currentSubscription?.duration,
              currentSubscription?.nextPayment
            ),
            interpolation: {
              escapeValue: false,
            },
          })}
        </Message>
        <Message>
          {tb('iap.confirmation.upgrade.nextPlan', {
            title: nextSubscription.title,
            price: translatePrice(
              nextSubscription.price,
              nextSubscription.currency,
              nextSubscription.duration
            ),
            interpolation: {
              escapeValue: false,
            },
          })}
        </Message>
        <ButtonContainer>
          <Button
            isFullWidth
            isLoading={isChangeLoading}
            onClick={handleChangeProduct}
            variant={ButtonVariant.Primary}
          >
            {tb('button.confirm')}
          </Button>
          <Button
            isFullWidth
            onClick={onChangeCancel}
            variant={ButtonVariant.Secondary}
          >
            {tb('button.cancel')}
          </Button>
        </ButtonContainer>
      </FullPageModal.CenteredContent>
    </FullPageModal>
  );
};
